import { CookieCategory } from '@amzn/awscat-cookie-management';
import { JsonObject } from 'json2typescript';

import { ChartFunctions, ChartTypes } from '../components/portfolio-mgmt/dashboard/constants';
import { GetLocalStorage, SetLocalStorage } from '../contexts/CookieConsentContext';

const dashboardStorageKey = 'dashboard:portfolio:';

export interface Partition {
    labels: string[];
    upperBounds: number[];
}
export interface DashboardChart {
    id: string;
    type: ChartTypes;
    title: string;
    description: string;
    withLabels?: boolean;
    sourceName?: string;
    category: string | {
        prop: string;
        partition?: Partition;
    };
    value: {
        fn: ChartFunctions;
        prop: string;
    };
}

export interface Dashboard {
    charts: DashboardChart[];
}

@JsonObject('DashboardsLocalStorage')
class DashboardsLocalStorage {
    [dashboardName: string]: {
        charts: DashboardChart[];
    }

    private static getDashboards(portfolioId: string, getLocalStorage: GetLocalStorage): DashboardsLocalStorage {
        try {
            const dashboardValue: string = getLocalStorage(`${dashboardStorageKey}${portfolioId}`, [CookieCategory.Functional]);
            if (dashboardValue) {
                const dashboards: DashboardsLocalStorage = JSON.parse(dashboardValue);
                return dashboards;
            }
        } catch (err) {
            console.log(err);
        }
        return {};
    }

    public static enumerateDashboards(portfolioId: string, getLocalStorage: GetLocalStorage) {
        const dashboards = this.getDashboards(portfolioId, getLocalStorage);
        return Object.keys(dashboards);
    }

    public static loadDashboard(portfolioId: string, dashboardName: string, getLocalStorage: GetLocalStorage) {
        const dashboards = this.getDashboards(portfolioId, getLocalStorage);
        return dashboards[dashboardName];
    }

    public static saveDashboard(
        portfolioId: string, dashboardName: string, dashboard: Dashboard, getLocalStorage: GetLocalStorage, setLocalStorage: SetLocalStorage
    ) {
        const dashboards = this.getDashboards(portfolioId, getLocalStorage);
        dashboards[dashboardName] = dashboard;
        // Cannot use JSONConverter serializer because the value of this localStorage has no fixed key,
        // instead the keys are portfolio IDs, and are not recognized and therefore ignored when mapping JSON
        const str = JSON.stringify(dashboards);
        setLocalStorage(`${dashboardStorageKey}${portfolioId}`, str, [CookieCategory.Functional]);
    }

    public static deleteDashboard(portfolioId: string, dashboardName: string, getLocalStorage: GetLocalStorage, setLocalStorage: SetLocalStorage) {
        const dashboards = this.getDashboards(portfolioId, getLocalStorage);
        delete dashboards[dashboardName];
        const str = JSON.stringify(dashboards);
        setLocalStorage(`${dashboardStorageKey}${portfolioId}`, str, [CookieCategory.Functional]);
    }
}

export default DashboardsLocalStorage;
